import React, { useState, useEffect, useCallback, useContext } from "react";
import { getFlags as fetchUserFlags } from "../utils/api";
import { getFlags as fetchAdminFlags } from "../utils/adminApi";
import { getAdminAccessToken } from "../utils/localStorageService";
import { dedupeRequest } from "../utils/dedupeRequest";

const FLAGS_KEY = "feature_flags";
const FLAGS_TIMESTAMP_KEY = "feature_flags_timestamp";
const TTL_MINUTES = 10;
const FLAGS_FETCH_KEY = "feature_flags_fetch";

const FlagsContext = React.createContext();

export function useFlags() {
  const context = useContext(FlagsContext);
  if (!context) throw new Error("useFlags must be used within a FlagsProvider");
  return context;
}

export function FlagsProvider({ children }) {
  const [flags, setFlags] = useState(null);
  const [areFlagsLoading, setAreFlagsLoading] = useState(true);

  const getCachedFlags = () => {
    const flagsStr = localStorage.getItem(FLAGS_KEY);
    const timestamp = localStorage.getItem(FLAGS_TIMESTAMP_KEY);
    if (!flagsStr || !timestamp) return null;
    const age = (Date.now() - parseInt(timestamp, 10)) / 1000 / 60;
    if (age > TTL_MINUTES) return null;
    try {
      return JSON.parse(flagsStr);
    } catch {
      return null;
    }
  };

  const saveFlags = (flagsObj) => {
    localStorage.setItem(FLAGS_KEY, JSON.stringify(flagsObj));
    localStorage.setItem(FLAGS_TIMESTAMP_KEY, Date.now().toString());
  };

  const clearFlags = useCallback(() => {
    localStorage.removeItem(FLAGS_KEY);
    localStorage.removeItem(FLAGS_TIMESTAMP_KEY);
    setFlags(null);
  }, []);

  const fetchFlags = useCallback(async () => {
    setAreFlagsLoading(true);
    try {
      const fetchedFlags = await dedupeRequest(FLAGS_FETCH_KEY, async () => {
        if (getAdminAccessToken()) {
          return fetchAdminFlags();
        }
        return fetchUserFlags();
      });

      if (fetchedFlags) {
        setFlags(fetchedFlags);
        saveFlags(fetchedFlags);
      } else {
        setFlags((prev) => prev || {});
      }
    } catch (e) {
      setFlags((prev) => prev || {});
    } finally {
      setAreFlagsLoading(false);
    }
  }, []);

  useEffect(() => {
    const cached = getCachedFlags();
    if (cached) {
      setFlags(cached);
      setAreFlagsLoading(false);
      fetchFlags();
      return;
    }
    fetchFlags();
  }, [fetchFlags]);

  useEffect(() => {
    const onLogout = () => clearFlags();
    const onLogin = () => {
      clearFlags();
      fetchFlags();
    };
    window.addEventListener("login", onLogin);
    window.addEventListener("logout", onLogout);
    return () => {
      window.removeEventListener("login", onLogin);
      window.removeEventListener("logout", onLogout);
    };
  }, [clearFlags, fetchFlags]);

  return (
    <FlagsContext.Provider value={{ flags, areFlagsLoading, clearFlags }}>
      {children}
    </FlagsContext.Provider>
  );
}
